import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { PlainButton, useIsMobile } from 'components/v3';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';

// Pick fields and put them in order (design sub_modals.jsx FieldChooser),
// shared by the Columns and Export dialogs. Left: every field grouped like
// the form, with a search and a check per group. Right: the picked fields
// in column order — drag to reorder, or use the up / down / remove buttons.
// On phones the two halves are tabs.

const Root = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: ${(p) =>
    p.$mobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)'};
  grid-template-rows: ${(p) =>
    p.$mobile ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  border-top: 1px solid ${(p) => sx(p.theme).line};
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin: 12px 16px 2px;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
`;

const TabButton = styled(PlainButton)`
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
  background: ${(p) => (p.$on ? sx(p.theme).fill : 'transparent')};
  color: ${(p) => (p.$on ? sx(p.theme).text : sx(p.theme).label)};
`;

const Count = styled.span`
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  background: ${(p) => sx(p.theme).tint};
  color: ${(p) => sx(p.theme).otext};
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Pane = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  ${(p) => (p.$hidden ? 'display: none;' : '')}
`;

const AllPane = styled(Pane)`
  border-right: ${(p) => (p.$mobile ? '0' : `1px solid ${sx(p.theme).line}`)};
`;

const SelPane = styled(Pane)`
  background: ${(p) => (p.$mobile ? 'transparent' : sx(p.theme).inset)};
`;

const Search = styled.label`
  flex: none;
  display: flex;
  align-items: center;
  gap: 9px;
  height: 40px;
  margin: 14px 16px 4px;
  padding: 0 12px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).field};
  color: ${(p) => sx(p.theme).label};

  &:focus-within {
    border-color: ${ORANGE};
    box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.18);
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: none;
    outline: none;
    color: ${(p) => sx(p.theme).text};
    font-size: 14px;
    font-weight: 500;
  }
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${(p) => (p.$sel ? '2px 16px 16px' : '4px 10px 16px')};
`;

const GroupHead = styled(PlainButton)`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 8px 8px;
  background: ${(p) => sx(p.theme).card};
  text-align: left;
`;

const GroupTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: ${(p) => sx(p.theme).text2};
`;

const Muted = styled.span`
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(p) => sx(p.theme).label};
`;

const Item = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: ${(p) => (p.$mobile ? '46px' : '38px')};
  padding: 7px 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${(p) => (p.$on ? 600 : 500)};
  line-height: 1.3;
  text-align: left;
  color: ${(p) => (p.$on ? sx(p.theme).text : sx(p.theme).text2)};

  &:hover {
    background: ${(p) => sx(p.theme).hover};
  }
`;

const Check = styled.span`
  flex: none;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border-radius: 5px;
  border: 1.5px solid ${(p) => (p.$state ? ORANGE : sx(p.theme).ctl)};
  background: ${(p) => (p.$state ? ORANGE : 'transparent')};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SelHead = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${(p) => (p.$mobile ? '12px 16px 2px' : '20px 20px 2px')};
`;

const SelTitle = styled.span`
  flex: none;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).otext};
`;

const ClearButton = styled(PlainButton)`
  margin-left: auto;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => sx(p.theme).otext};

  &:hover {
    background: ${(p) => sx(p.theme).tint};
  }
`;

const Hint = styled.div`
  flex: none;
  padding: ${(p) => (p.$mobile ? '4px 16px 8px' : '4px 20px 10px')};
  font-size: 12.5px;
  color: ${(p) => sx(p.theme).label};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 50px;
  margin-bottom: 6px;
  padding: 6px 6px 6px 8px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid ${(p) => sx(p.theme).line};
  background: ${(p) => sx(p.theme).card};
  cursor: grab;
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};
  box-shadow: ${(p) => {
    if (p.$drop === 'before') return `0 -2px 0 ${ORANGE}`;
    if (p.$drop === 'after') return `0 2px 0 ${ORANGE}`;
    return 'none';
  }};

  &:hover {
    border-color: ${(p) => sx(p.theme).line2};
  }
`;

const Grip = styled.span`
  flex: none;
  display: flex;
  color: ${(p) => sx(p.theme).disabled};
`;

const Index = styled.span`
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${(p) => sx(p.theme).tint};
  color: ${(p) => sx(p.theme).otext};
  font-size: 11.5px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
`;

const RowText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .l,
  .g {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .l {
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => sx(p.theme).text};
  }

  .g {
    font-size: 11.5px;
    color: ${(p) => sx(p.theme).label};
  }
`;

const RowActions = styled.span`
  flex: none;
  display: flex;
  gap: 2px;
  opacity: ${(p) => (p.$mobile ? 1 : 0.5)};
  transition: opacity 120ms ease;

  ${Row}:hover &,
  &:focus-within {
    opacity: 1;
  }
`;

const RowButton = styled(PlainButton)`
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => sx(p.theme).text2};

  &:hover:not(:disabled) {
    background: ${(p) => (p.$x ? sx(p.theme).errorBg : sx(p.theme).tint)};
    color: ${(p) => (p.$x ? sx(p.theme).error : sx(p.theme).otext)};
  }

  &:disabled {
    opacity: 0.3;
  }
`;

const None = styled.div`
  padding: 28px 12px;
  text-align: center;
  font-size: 13.5px;
  color: ${(p) => sx(p.theme).label};
`;

const lower = (s) => String(s || '').toLowerCase();

const FieldChooser = ({
  catalog,
  value,
  onChange,
  selTitle,
  emptyText,
  tour,
}) => {
  const isMobile = useIsMobile();
  const byKey = useMemo(() => {
    const map = {};
    catalog.forEach((g) =>
      g.cols.forEach((c) => {
        map[c.key] = { ...c, group: g.label };
      })
    );
    return map;
  }, [catalog]);
  const total = Object.keys(byKey).length;
  const [query, setQuery] = useState('');
  const [pane, setPane] = useState('all');
  const [drag, setDrag] = useState(null);

  const q = lower(query.trim());
  const sel = value.filter((k) => byKey[k]);
  const has = (k) => sel.includes(k);
  const toggle = (k) =>
    onChange(has(k) ? sel.filter((x) => x !== k) : sel.concat(k));
  const setGroup = (group, on) =>
    onChange(
      on
        ? sel.concat(group.cols.map((c) => c.key).filter((k) => !has(k)))
        : sel.filter((k) => !group.cols.some((c) => c.key === k))
    );
  const move = (from, to) => {
    if (to < 0 || to >= sel.length || from === to) return;
    const next = sel.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const visible = catalog
    .map((g) => ({
      ...g,
      vis: g.cols.filter(
        (c) => !q || lower(c.label).includes(q) || lower(g.label).includes(q)
      ),
    }))
    .filter((g) => g.vis.length);

  const box = (state) => (
    <Check $state={state} aria-hidden="true">
      {state === 'on' ? <Glyph name="check" size={12} strokeWidth={3} /> : null}
      {state === 'some' ? (
        <Glyph name="minus" size={12} strokeWidth={3} />
      ) : null}
    </Check>
  );

  return (
    <Root $mobile={isMobile} data-tour={tour}>
      {isMobile ? (
        <Tabs role="tablist" aria-label="Fields">
          <TabButton
            role="tab"
            aria-selected={pane === 'all'}
            $on={pane === 'all'}
            onClick={() => setPane('all')}
          >
            All fields
            <Count>{total}</Count>
          </TabButton>
          <TabButton
            role="tab"
            aria-selected={pane === 'sel'}
            $on={pane === 'sel'}
            onClick={() => setPane('sel')}
          >
            {selTitle}
            <Count>{sel.length}</Count>
          </TabButton>
        </Tabs>
      ) : null}
      <AllPane $mobile={isMobile} $hidden={isMobile && pane !== 'all'}>
        <Search>
          <Glyph name="search" size={15} />
          <input
            value={query}
            placeholder="Find a field…"
            aria-label="Find a field"
            onChange={(e) => setQuery(e.target.value)}
          />
          {query ? (
            <PlainButton aria-label="Clear search" onClick={() => setQuery('')}>
              <Glyph name="x" size={14} />
            </PlainButton>
          ) : null}
        </Search>
        <Scroll>
          {visible.length === 0 ? (
            <None>{`No fields match “${query.trim()}”`}</None>
          ) : null}
          {visible.map((group) => {
            const on = group.cols.filter((c) => has(c.key)).length;
            let state = '';
            if (on === group.cols.length) state = 'on';
            else if (on) state = 'some';
            return (
              <div key={group.id} style={{ marginTop: 4 }}>
                <GroupHead
                  onClick={() => setGroup(group, state !== 'on')}
                  aria-label={`${state === 'on' ? 'Remove' : 'Add'} all in ${
                    group.label
                  }`}
                >
                  {box(state)}
                  <GroupTitle>{group.label}</GroupTitle>
                  <Muted>{`${on} / ${group.cols.length}`}</Muted>
                </GroupHead>
                {group.vis.map((c) => (
                  <Item
                    key={c.key}
                    $on={has(c.key)}
                    $mobile={isMobile}
                    aria-pressed={has(c.key)}
                    onClick={() => toggle(c.key)}
                  >
                    {box(has(c.key) ? 'on' : '')}
                    <span style={{ flex: 1, minWidth: 0 }}>{c.label}</span>
                  </Item>
                ))}
              </div>
            );
          })}
        </Scroll>
      </AllPane>
      <SelPane $mobile={isMobile} $hidden={isMobile && pane !== 'sel'}>
        <SelHead $mobile={isMobile}>
          <SelTitle>{selTitle}</SelTitle>
          <Muted>{`${sel.length} of ${total}`}</Muted>
          {sel.length ? (
            <ClearButton onClick={() => onChange([])}>Clear</ClearButton>
          ) : null}
        </SelHead>
        <Hint $mobile={isMobile}>
          Drag to reorder. This is the column order, left to right.
        </Hint>
        <Scroll $sel>
          {sel.length === 0 ? <None>{emptyText}</None> : null}
          {sel.map((k, i) => {
            const c = byKey[k];
            let drop = null;
            if (drag && drag.over === i && drag.from !== i) {
              drop = drag.from < i ? 'after' : 'before';
            }
            return (
              <Row
                key={k}
                draggable
                $dragging={!!drag && drag.from === i}
                $drop={drop}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  try {
                    e.dataTransfer.setData('text/plain', k);
                  } catch (er) {
                    // Some browsers refuse custom data; the order still moves.
                  }
                  setDrag({ from: i, over: i });
                }}
                onDragOver={(e) => {
                  if (!drag) return;
                  e.preventDefault();
                  if (drag.over !== i) setDrag({ from: drag.from, over: i });
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (drag) move(drag.from, i);
                  setDrag(null);
                }}
                onDragEnd={() => setDrag(null)}
              >
                <Grip data-help="drag-handle" aria-hidden="true">
                  <Glyph name="grip-vertical" size={14} />
                </Grip>
                <Index>{i + 1}</Index>
                <RowText>
                  <span className="l">{c.label}</span>
                  <span className="g">{c.group}</span>
                </RowText>
                <RowActions $mobile={isMobile}>
                  <RowButton
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0}
                    aria-label={`Move ${c.label} up`}
                  >
                    <Glyph name="chevron-up" size={15} />
                  </RowButton>
                  <RowButton
                    onClick={() => move(i, i + 1)}
                    disabled={i === sel.length - 1}
                    aria-label={`Move ${c.label} down`}
                  >
                    <Glyph name="chevron-down" size={15} />
                  </RowButton>
                  <RowButton
                    $x
                    onClick={() => toggle(k)}
                    aria-label={`Remove ${c.label}`}
                  >
                    <Glyph name="x" size={15} />
                  </RowButton>
                </RowActions>
              </Row>
            );
          })}
        </Scroll>
      </SelPane>
    </Root>
  );
};

FieldChooser.propTypes = {
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      cols: PropTypes.arrayOf(PropTypes.object).isRequired,
    })
  ).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  selTitle: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
  // The product tour's target name for this chooser.
  tour: PropTypes.string,
};

FieldChooser.defaultProps = { tour: undefined };

export default FieldChooser;
